import {
	Form,
	Toolbar,
	ValidationError,
	useMentionInputIntermediateState,
	MentionInputIntermediateStateHandlers
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	CommentFormData,
	createFormDataForEdit,
	createSaveModel,
	validationConfiguration
} from "../CommentForm/CommentForm";
import styles from "./EditForm.module.less";
import {
	HubCommentMediaPreviewModel,
	HubCommentModel,
	HubCommentSaveModel,
	UserMentionViewModel
} from "client/Api/Hub/common";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import { HubCommentSectionService } from "../HubCommentSection";
import { CommentComposer } from "./CommentComposer";
import { isProseMirror } from "client/Components/MentionInput/types";
import { getModel } from "client/Components/MentionInput/converters";
import { HubCommentServerError, tryParseHubCommentServerError } from "../MobileComment/MobileCommentForm/MobileCommentForm";
import { HubCommentOptionsModel } from "client/Api/Hub/common.details";
import { isNotNil } from "client/Misc/utils";

export type EditFormProps = {
	commentId: number;
	message: string;
	mentions: readonly UserMentionViewModel[];
	onSave: (comment: HubCommentModel) => void;
	onCancel(): void;
	service: HubCommentSectionService;
	media: HubCommentMediaPreviewModel | null;
	commentOptions: HubCommentOptionsModel;
};

export const EditForm = (props: EditFormProps): ReactElement => {
	const {
		commentId,
		message: initialMessage,
		mentions: initialMentions,
		media: initialMedia,
		onSave,
		onCancel,
		service,
		commentOptions
	} = props;

	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [formData, setFormData] = useState<CommentFormData>(
		() => createFormDataForEdit(
			initialMessage,
			initialMentions
		)
	);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const intermediateState: MentionInputIntermediateStateHandlers = useMentionInputIntermediateState();
	const formHandler: IFormHandler<CommentFormData> = useFormHandler<CommentFormData>(
		formData,
		setFormData,
		validationConfiguration
	);

	const formText = useMemo(
		(): string | undefined => {
			return isProseMirror(intermediateState.intermediateState)
				? getModel(
					intermediateState.intermediateState
						.editorState
				).text
				: undefined;
		},
		[intermediateState]
	);

	const isEmpty: boolean = formText?.length === 0;
	const isMessageDifferent: boolean = formText !== initialMessage;

	const canSave = useMemo(
		(): boolean => {
			if (isSaving) {
				return false;
			}

			const { media } = formData;

			const hasMedia: boolean = (!media.isSet && isNotNil(initialMedia))
				|| (media.isSet && isNotNil(media.value));

			if (isEmpty && !hasMedia) {
				return false;
			}

			const isMediaDifferent: boolean = formData.media
				.isSet;

			return isMessageDifferent || isMediaDifferent;
		},
		[
			formData,
			isSaving,
			initialMedia,
			isEmpty,
			isMessageDifferent
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isSaving) {
				return;
			}

			setIsSaving(true);
			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsSaving(false);
					return;
				}

				const model: HubCommentSaveModel = createSaveModel(formData);

				const result: HubCommentModel = await service.editComment(
					commentId,
					model,
					globalCancellation
				);
				onSave(result);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (formHandler.validation.tryProcessServerError(e)) {
					const result: TryResult<HubCommentServerError> = tryParseHubCommentServerError(e);
					if (!result.wasSuccessful) {
						return;
					}

					const mediaResult: string | undefined = result.result
						.media;

					if (isNotNil(mediaResult)) {
						alertManager.showError(mediaResult);

						formHandler.for("media")
							.reducer
							.setValue({
								isSet: true,
								value: null
							});

						formHandler.for("media")
							.validation
							.triggerValueChanged();
					}
				} else {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

			} finally {
				setIsSaving(false);
			}
		},
		[
			alertManager,
			commentId,
			globalCancellation,
			formHandler,
			formData,
			isSaving,
			onSave,
			service
		]
	);

	return (
		<Form trackingId="edit-comment" onSubmit={saveHandler}>
			<div className={styles.editCommentFormFields}>
				<CommentComposer
					{...formHandler.for("message").attachToMentionInput(intermediateState, formData.mentionData)}
					autoFocus="focus"
					service={service}
					onFormDataChange={setFormData}
					formData={formData}
					initialMedia={initialMedia}
					trackingId="edit-comment-composer"
					options={commentOptions}
				/>
				<ValidationError {...formHandler.for("message").attachToValidationError()} />
			</div>
			<Toolbar>
				<Toolbar.Button
					trackingId="save"
					kind="primary"
					text={localize("_Save")}
					isDisabled={!canSave}
					onClick={saveHandler}
				/>
				<Toolbar.Button
					trackingId="cancel"
					kind="secondary"
					text={localize("_Cancel")}
					onClick={onCancel}
				/>
			</Toolbar>
		</Form>
	);
};

EditForm.displayName = "EditForm";
